import { Link } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthProvider";
import { accountConsoleUrl } from "../../auth/realms";
import { chooseLanguage, LANGUAGES } from "../../i18n";
import { ServersDialog } from "../../servers/ServersDialog";
import { useServerName } from "../../ui/EnvironmentBar";
import { UsageStatsSwitch } from "./UsageStatsSwitch";

function initials(name: string): string {
  const parts = name.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/**
 * User menu (WEB-F-SH-03): language (WEB-F-SH-04), the Keycloak account console for passkeys, sign out, and with
 * `servers` the list of Creavision servers (WEB-SRV-LST-01). In the customer app it holds the usage statistics switch
 * (usage-metrics UM-05); staff are never counted (UM-04), so the console has none. With `notifications` it links
 * the Notifications page (specs/alarm-email WEB-AE-01).
 */
export function UserMenu({
  name,
  servers = false,
  notifications = false,
}: {
  name: string;
  servers?: boolean;
  notifications?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { realm, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [listing, setListing] = useState(false);
  const server = useServerName();
  const panelId = useId();
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onClick = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-brand text-sm font-semibold text-brand-trace"
      >
        <span aria-hidden="true">{initials(name)}</span>
        <span className="sr-only">{t("user.menu", { name })}</span>
      </button>
      {open && (
        <div
          id={panelId}
          className="absolute right-0 z-20 mt-2 w-64 rounded-md border border-gray-200 bg-white p-2 text-sm shadow-lg"
        >
          <p className="truncate px-2 py-1 text-gray-700">{name}</p>
          {server && <p className="px-2 pb-1 text-xs text-gray-700">{t("environment.server", { name: server })}</p>}
          <fieldset className="mt-1 border-t border-gray-100 px-2 pt-2">
            <legend className="text-xs font-medium text-gray-700">{t("user.language")}</legend>
            <div className="mt-1 flex gap-2">
              {LANGUAGES.map((lng) => (
                <button
                  key={lng}
                  type="button"
                  aria-pressed={i18n.resolvedLanguage === lng}
                  onClick={() => chooseLanguage(lng)}
                  className="rounded-md border border-gray-300 px-2 py-1 aria-pressed:border-brand aria-pressed:bg-brand aria-pressed:text-brand-trace"
                >
                  {t(`language.${lng}`)}
                </button>
              ))}
            </div>
          </fieldset>
          {realm === "customers" && <UsageStatsSwitch />}
          <div className="mt-2 border-t border-gray-100 pt-2">
            {notifications && (
              <Link
                to="/notifications"
                onClick={() => setOpen(false)}
                className="block rounded-md px-2 py-1.5 hover:bg-gray-100"
              >
                {t("notifications.title")}
              </Link>
            )}
            <a
              href={accountConsoleUrl(realm)}
              target="_blank"
              rel="noreferrer"
              className="block rounded-md px-2 py-1.5 hover:bg-gray-100"
            >
              {t("user.account")}
            </a>
            {servers && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setListing(true);
                }}
                className="block w-full rounded-md px-2 py-1.5 text-left hover:bg-gray-100"
              >
                {t("servers.open")}
              </button>
            )}
            <button
              type="button"
              onClick={() => void signOut()}
              className="block w-full rounded-md px-2 py-1.5 text-left hover:bg-gray-100"
            >
              {t("user.signOut")}
            </button>
          </div>
        </div>
      )}
      {servers && <ServersDialog open={listing} onClose={() => setListing(false)} />}
    </div>
  );
}
